import React, { useState } from "react";
import useAdminMutation from "@/hooks/useAdminMutation";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLShopLowStockThresholdCard — RACKET-LAB-ROLE-3 (Feb 2026).
 *
 * Inline admin-only block to view / edit the per-shop default
 * low-stock threshold (`default_low_stock_threshold` inside
 * `racket_lab_shops.settings`).
 *
 * Behaviour:
 *   - admin / staff (workforce write strict gate) can edit & save.
 *   - shop_manager / operator / qa_manager / qa_tester see READ-ONLY
 *     (the PATCH endpoint will 403 anyway; we hide the form for them).
 *   - Empty state when the field is null.
 *   - Saving re-PATCHes `settings` with the full ShopSettings payload
 *     so unrelated keys are preserved verbatim.
 *
 * Low-stock alerts already shipped in SS-6.1 (per-item threshold).
 * This card adds a shop-level FALLBACK that is applied to items
 * without a per-item threshold. Behaviour for items WITH a per-item
 * threshold is unchanged.
 */
const RLShopLowStockThresholdCard = ({ shop, onSaved }) => {
  const t = useRacketLabAdminT();
  const { ctx } = useRLContext();
  const { run, loading } = useAdminMutation();
  const canEdit = ctx?.profile === "admin" || ctx?.profile === "staff";
  const settings = shop?.settings || {};

  const [threshold, setThreshold] = useState(
    settings.default_low_stock_threshold == null
      ? ""
      : String(settings.default_low_stock_threshold)
  );

  const hasThreshold = settings.default_low_stock_threshold != null;

  const onSave = async () => {
    const next = {
      ...settings,
      default_low_stock_threshold:
        threshold === "" ? null : parseFloat(threshold),
    };
    const updated = await run("patch", `/admin/racket-lab/shops/${shop.id}`, {
      settings: next,
    });
    if (updated) onSaved?.(updated);
  };

  const onClear = async () => {
    const next = { ...settings, default_low_stock_threshold: null };
    const updated = await run("patch", `/admin/racket-lab/shops/${shop.id}`, {
      settings: next,
    });
    if (updated) onSaved?.(updated);
  };

  return (
    <section
      data-testid="rl-shop-low-stock-threshold-card"
      className="rounded-md border border-slate-200 bg-white p-4 md:col-span-2"
    >
      <header className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          {t("shops.detail.low_stock_threshold.title")}
        </h3>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">
          {t("shops.detail.low_stock_threshold.admin_only")}
        </span>
      </header>
      <p className="mb-3 text-[11px] text-slate-500">
        {t("shops.detail.low_stock_threshold.hint")}
      </p>

      {!hasThreshold && (
        <p
          data-testid="rl-shop-low-stock-threshold-empty"
          className="mb-3 rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500"
        >
          {t("shops.detail.low_stock_threshold.empty")}
        </p>
      )}

      {!canEdit && hasThreshold && (
        <div data-testid="rl-shop-low-stock-threshold-readonly" className="text-xs">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">
            {t("shops.detail.low_stock_threshold.value")}
          </p>
          <p className="font-semibold">{settings.default_low_stock_threshold}</p>
        </div>
      )}

      {canEdit && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="flex flex-col gap-1 md:col-span-2">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">
              {t("shops.detail.low_stock_threshold.value")}
            </span>
            <input
              data-testid="rl-shop-low-stock-threshold-input"
              type="number"
              min={0}
              max={100000}
              step="0.1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="—"
            />
          </label>
          <div className="flex items-end gap-2 md:col-span-2">
            <button
              type="button"
              data-testid="rl-shop-low-stock-threshold-save"
              onClick={onSave}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {loading ? t("shops.detail.low_stock_threshold.saving") : t("shops.detail.low_stock_threshold.save")}
            </button>
            {hasThreshold && (
              <button
                type="button"
                data-testid="rl-shop-low-stock-threshold-clear"
                onClick={onClear}
                disabled={loading}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed"
              >
                {t("shops.detail.low_stock_threshold.clear")}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default RLShopLowStockThresholdCard;
